import * as React from "react"
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"

// Simple Toast Context
const ToastContext = React.createContext(null)

export function useSimpleToast() {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error('useSimpleToast must be used within SimpleToastProvider')
  }
  return context
}

// Toast Item Component
const ToastItem = ({ toast, onClose }) => {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
  }

  const variants = {
    success: "border-green-200 bg-green-50",
    error: "border-red-200 bg-red-50",
    info: "border-blue-200 bg-blue-50",
    warning: "border-yellow-200 bg-yellow-50",
  }

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id)
    }, toast.duration || 4000)
    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, onClose])

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border shadow-lg bg-white animate-in slide-in-from-right-full duration-300",
        variants[toast.variant] || variants.info
      )}
      role="alert"
    >
      {icons[toast.variant] || icons.info}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="font-semibold text-sm text-gray-900">{toast.title}</p>
        )}
        {toast.description && (
          <p className="text-sm text-gray-600 mt-0.5">{toast.description}</p>
        )}
      </div>
      <button
        onClick={() => onClose(toast.id)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

// Toast Provider
export function SimpleToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([])
  const toastRefs = React.useRef(new Map())

  const addToast = React.useCallback((toast) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    setToasts(prev => [...prev, { ...toast, id }])
    return id
  }, [])

  const removeToast = React.useCallback((id) => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    })
  }, [])

  const toast = React.useMemo(() => ({
    success: (description, title = "Éxito") => addToast({ variant: "success", title, description }),
    error: (description, title = "Error") => addToast({ variant: "error", title, description }),
    info: (description, title = "Info") => addToast({ variant: "info", title, description }),
    warning: (description, title = "Aviso") => addToast({ variant: "warning", title, description }),
  }), [addToast])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast Container - positioned fixed, rendered inline (no portal) */}
      {toasts.length > 0 && (
        <div 
          className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
          aria-live="polite"
        >
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem toast={t} onClose={removeToast} />
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}

// Export a standalone toast function for use outside React
let toastFn = null

export function setToastFunction(fn) {
  toastFn = fn
}

export const toast = {
  success: (message) => toastFn?.success(message),
  error: (message) => toastFn?.error(message),
  info: (message) => toastFn?.info(message),
  warning: (message) => toastFn?.warning(message),
}
